import { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import useTranslate from '../../../i18n/useTranslate'
import { ClosedDataListType } from '../../../services/um/useSearchClosed'
import CustomPagination from '../closed/CustomPagination'
import CustomNoRowsOverlay from '../closed/CustomNoRowsOverlay'
import { CertificateDetail } from '../../../services/um/useCertificateSummary'

type DataRowsType = {
    id: string
    projectNumber: string
    certificateNo?: string
    certificateType?: string
    name: string
    projectType: string
    projectNameTH: string
    projectNameEN: string
}

type ClosePanelComponentProps = {
    dataList: CertificateDetail[]
}

const Table = ({ dataList }: ClosePanelComponentProps) => {
    const classes = useStyles()
    const translate = useTranslate()
    const lang = translate.languageStore.currentLanguage as 'th' | 'en'
    const [rows, setRows] = useState<DataRowsType[]>([])

    useEffect(() => {
        convertData()
    }, [dataList, lang])

    const convertData = () => {
        if (dataList.length > 0) {
            let rows = dataList.map((item: CertificateDetail, index: number) => {
                return {
                    id: `${item.projectID ?? '-'}|${item.certificateType ?? '-'}|${item.certificateNo ?? '-'}|${index}`,
                    projectNumber: item.projectNumber ?? '-',
                    certificateNo: item.certificateNo ?? '-',
                    certificateType: item.certificateType ?? '-',
                    name: item.name?.[lang] ?? '-',
                    projectNameTH: item.projectName?.th ?? '-',
                    projectNameEN: item.projectName?.en ?? '-',
                    projectType: item.projectType?.name?.[lang] ?? '-',
                }
            })
            setRows(rows)
        } else {
            setRows([])
        }
    }

    const column: GridColDef[] = [
        {
            field: 'projectNumber',
            headerName: `${translate.get('UM.CERTIFICATE_SUMMARY.DATA_TABLE.COLUMN1')}`,
            width: 180,
            headerClassName: 'header-table',
            headerAlign: 'left',
            sortable: true,
        },
        {
            field: 'certificateNo',
            headerName: `${translate.get('UM.CERTIFICATE_SUMMARY.DATA_TABLE.COLUMN4')}`,
            width: 180,
            headerClassName: 'header-table',
            headerAlign: 'left',
            sortable: true,
        },
        {
            field: 'certificateType',
            headerName: `${translate.get('UM.CERTIFICATE_SUMMARY.DATA_TABLE.COLUMN5')}`,
            width: 180,
            headerClassName: 'header-table',
            headerAlign: 'left',
            sortable: true,
        },
        {
            field: 'name',
            headerName: `${translate.get('UM.CERTIFICATE_SUMMARY.DATA_TABLE.COLUMN6')}`,
            width: 300,
            headerClassName: 'header-table',
            headerAlign: 'left',
            sortable: true,
        },
        {
            field: 'projectType',
            headerName: `${translate.get('UM.CERTIFICATE_SUMMARY.DATA_TABLE.COLUMN7')}`,
            width: 200,
            headerClassName: 'header-table',
            headerAlign: 'left',
            sortable: true,
        },
        {
            field: 'projectNameTH',
            headerName: `${translate.get('UM.CERTIFICATE_SUMMARY.DATA_TABLE.COLUMN8')}`,
            width: 400,
            headerClassName: 'header-table',
            headerAlign: 'left',
            sortable: true,
        },
        {
            field: 'projectNameEN',
            headerName: `${translate.get('UM.CERTIFICATE_SUMMARY.DATA_TABLE.COLUMN9')}`,
            width: 400,
            headerClassName: 'header-table',
            headerAlign: 'left',
            sortable: true,
        },
    ]

    return (
        <div style={{ width: '100%', marginTop: 16 }}>
            <DataGrid
                className={classes.root}
                rows={rows}
                columns={column}
                autoHeight={true}
                pagination
                pageSize={10}
                rowsPerPageOptions={[10]}
                sortingOrder={['asc', 'desc']}
                disableColumnMenu={true}
                disableSelectionOnClick={true}
                components={{
                    Pagination: CustomPagination,
                    NoRowsOverlay: CustomNoRowsOverlay,
                }}
            />
        </div>
    )
}

const useStyles = makeStyles((theme) => ({
    root: {
        '& .super-app-theme--fffff': {
            backgroundColor: '#DB5F8E',
            '&:hover': {
                backgroundColor: '#DB5F8E',
            },
        },
        '& .super-app-theme--header': {
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
        },
        '& .MuiDataGrid-columnSeparator': {
            visibility: 'hidden',
        },
        '& .MuiDataGrid-row': {
            lineHeigh: '70px',
        },

        '& .header-table': {
            backgroundColor: '#DB5F8E',
            color: '#FFFFFF',
        },
        '& .MuiDataGrid-iconSeparator': {
            display: 'none',
        },
        '& .MuiDataGrid-sortIcon': {
            color: '#FFFFFF',
        },
        '& .MuiBadge-anchorOriginTopRightRectangle': {
            display: 'none',
        },
        '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        '& .MuiDataGrid-columnHeaderWrapper': {
            backgroundColor: '#DB5F8E',
            '& .MuiCheckbox-root': {
                color: '#FFFFFF',
            },
            '& .MuiCheckbox-root.Mui-checked': {
                color: '#FFFFFF',
            },
        },
        width: '100%',
        marginTop: 16,
        overflowX: 'hidden',
    },
}))

export default Table
